/**
 * Wave POS · Loading matrix pass.
 *
 * TimedOutState — distinct state for when a query/fetch takes more than
 * 8 seconds without a real error being thrown (stale promise / slow network).
 *
 * Differentiated from PageErrorState:
 *   - timeout (slow) → TimedOutState (clock icon, softer tone)
 *   - real error (4xx/5xx) → PageErrorState (alert icon, err colour)
 *
 * Tokens: text-ink, text-ink-muted, bg-surface, border-line, text-warn, brand-500
 * Mobile: min-h-[44px] on interactive targets, safe-area-aware padding.
 */

import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

export interface TimedOutStateProps {
  /** Title. Defaults to locale-sensitive default (pt-BR). */
  title?: string;
  /** Description. Defaults to locale-sensitive default. */
  description?: string;
  /** Retry button label. */
  retryLabel?: string;
  /** Called when user clicks retry. */
  onRetry?: () => void;
  /** Layout variant. 'page' (default) fills min-h-screen; 'inline' fits inside a sub-section. */
  variant?: 'page' | 'inline';
  className?: string;
}

/**
 * Shown when a fetch / Suspense boundary hasn't resolved after ~8s
 * but no explicit error was thrown.  Softer than PageErrorState.
 */
export function TimedOutState({
  title,
  description,
  retryLabel,
  onRetry,
  variant = 'page',
  className,
}: TimedOutStateProps) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const resolvedTitle = title ?? (isEn ? 'Taking longer than usual' : 'Está demorando mais que o normal');
  const resolvedDescription = description ?? (isEn ? 'Could be the connection. Want to try again?' : 'Pode ser a conexão. Quer tentar de novo?');
  const resolvedRetryLabel = retryLabel ?? (isEn ? 'Try again' : 'Tentar novamente');
  const containerClass =
    variant === 'inline'
      ? 'flex w-full items-center justify-center py-8'
      : 'flex min-h-screen flex-col items-center justify-center gap-6 bg-surface px-6 pb-[max(24px,env(safe-area-inset-bottom))] pt-[env(safe-area-inset-top)]';

  return (
    <div
      className={cn(
        containerClass,
        variant === 'inline' && 'flex-col gap-4',
        className,
      )}
    >
      {/* Icon */}
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-warn/10">
        <ClockIcon className="h-7 w-7 text-warn" />
      </div>

      {/* Text block — role="status" (polite live region) so screen readers
          announce the slow-load state without the urgency of a hard error. */}
      <div role="status" aria-live="polite" className="max-w-[65ch] space-y-2 text-center">
        <p className="text-[17px] font-semibold leading-snug text-ink">{resolvedTitle}</p>
        <p className="text-[14px] leading-relaxed text-ink-muted">{resolvedDescription}</p>
      </div>

      {/* Retry button */}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={cn(
            'min-h-[44px] rounded-xl bg-brand-500 px-6 text-[14px] font-semibold text-white',
            'transition-colors duration-120 ease-out-soft',
            'active:scale-[0.97] active:transition-none',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
          )}
        >
          {resolvedRetryLabel}
        </button>
      )}
    </div>
  );
}
